// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * How the blocks on a sheet are laid out and sized.
 *
 * These are drawing annotations, not interface chrome. They are authored in
 * the drawing's own coordinates and scale with it, so a legend that is a
 * quarter of the sheet on screen is a quarter of the sheet on ARCH D as well
 * — which is how it ended up looking enormous. Hence a size multiplier the
 * user sets, and a legend that reflows into columns when it is dragged
 * wider, instead of one tall strip down the side of the drawing.
 *
 * The type ladder here is separate from the interface's on purpose. A title
 * block at 7 units is right on a sheet and would be unreadable in a panel;
 * they are different media and sharing one scale between them would make
 * both wrong.
 */

/**
 * Annotation type sizes, in POINTS on paper.
 *
 * Not in screen units, and not as a fraction of the sheet. A drawing is
 * authored at a size, and 9pt is 9pt whether it is printed on letter or on
 * ARCH D -- the sheet getting bigger means more room for the same text, not
 * bigger text. Sizing these as a share of the sheet made a legend that was
 * reasonable on a small sheet enormous on a large one.
 */
export const SHEET_TYPE = {
  /** A block's heading: LEGEND, the company name. */
  head: 9,
  /** The big one: the sheet number. */
  sheetNo: 12,
  /** An ordinary row of the title block. */
  value: 8,
  /** Its label, and the legend's entries. */
  label: 7.5,
  /** Revision rows and the scale bar's ticks. */
  small: 7,
  /** The abbreviation inside a legend swatch. */
  swatch: 5.5,
} as const;

/**
 * How many drawing units there are to an inch of paper.
 *
 * The drawing is authored in a space as wide as the sheet is, whatever that
 * happens to be on screen; this is the bridge to real paper. Everything a
 * printer will see should be sized through it.
 */
export function unitsPerInch(spaceWidth: number, sheetWidthInches: number | null | undefined): number {
  if (!Number.isFinite(spaceWidth) || spaceWidth <= 0) return 96;
  if (!sheetWidthInches || !Number.isFinite(sheetWidthInches) || sheetWidthInches <= 0) return 96;
  return spaceWidth / sheetWidthInches;
}

/** One point, in drawing units. */
export function pt(unitsPerIn: number): number {
  return unitsPerIn / 72;
}

export interface BlockBox { x: number; y: number; w: number; h: number }

/**
 * A block's id for the purpose of selection.
 *
 * Blocks share the drawing's one selection rather than keeping a second one
 * beside it: everything that already lets go of a selection -- clicking bare
 * canvas, Escape, picking something else -- then lets go of a block too,
 * without any of it having to learn what a block is.
 */
const BLOCK_SEL = "block:";
export function blockSelId(kind: string): string { return BLOCK_SEL + kind; }
export function isBlockSel(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith(BLOCK_SEL);
}
export function blockSelKind(id: string | null | undefined): string | null {
  return isBlockSel(id) ? (id as string).slice(BLOCK_SEL.length) : null;
}

/** What a legend needs, given how many entries and how wide it is drawn. */
export interface LegendLayout {
  cols: number;
  /** How many entries in the tallest column. */
  perCol: number;
  w: number; h: number;
  rowH: number; colW: number;
}

/** A legend column, in inches of paper. Wide enough for a swatch and a
 *  name at 7.5pt, which is what decides it. */
export const LEGEND_COL_IN = 1.5;
/** And a row. */
export const LEGEND_ROW_IN = 0.15;
export const LEGEND_MAX_COLS = 4;

/**
 * The legend's shape at a given width.
 *
 * Columns come from the width rather than being set separately: dragging the
 * corner is the whole control, and a column count that could disagree with
 * the box it is drawn in is a second source of truth.
 */
export function legendLayout(
  rowCount: number, width: number | undefined, scale = 1, unitsPerIn = 96,
): LegendLayout {
  const rowH = LEGEND_ROW_IN * unitsPerIn * scale;
  const colW = LEGEND_COL_IN * unitsPerIn * scale;
  const rows = Math.max(0, Math.floor(rowCount));
  const wanted = Math.max(colW, Number.isFinite(width as number) ? (width as number) : colW);
  const cols = Math.max(1, Math.min(LEGEND_MAX_COLS, Math.round(wanted / colW), rows || 1));
  const perCol = rows ? Math.ceil(rows / cols) : 0;
  return {
    cols, perCol, rowH, colW,
    w: cols * colW,
    // Heading, the rows, and a little air underneath.
    h: (0.17 + perCol * LEGEND_ROW_IN + 0.13) * unitsPerIn * scale,
  };
}

/** Which column and row an entry falls in, filling each column before the
 *  next so a list still reads top to bottom. */
export function legendSlot(index: number, perCol: number): { col: number; row: number } {
  if (perCol <= 0) return { col: 0, row: 0 };
  return { col: Math.floor(index / perCol), row: index % perCol };
}

/**
 * The width that gives a legend a particular height.
 *
 * Dragging the bottom edge up should make a legend wider, not clip it: fewer
 * rows per column means more columns. This turns a target height back into
 * the width that produces it, so both axes of the corner grip do something
 * and neither has to be a separate setting.
 */
export function widthForHeight(
  rowCount: number, targetH: number, scale = 1, unitsPerIn = 96,
): number {
  const col = LEGEND_COL_IN * unitsPerIn * scale;
  const rows = Math.max(0, Math.floor(rowCount));
  if (!rows || !Number.isFinite(targetH)) return col;
  const chrome = 0.3 * unitsPerIn * scale;
  const row = LEGEND_ROW_IN * unitsPerIn * scale;
  const perCol = Math.max(1, Math.round((targetH - chrome) / row));
  const cols = Math.max(1, Math.min(LEGEND_MAX_COLS, Math.ceil(rows / perCol)));
  return cols * col;
}
/**
 * Text broken into lines that fit a width.
 *
 * SVG will not wrap for you, so a long project name ran out of the title
 * block and over the drawing. Measured by an average character width rather
 * than by the browser: this has to give the same answer when the sheet is
 * rendered for export as it does on screen, and a measured width depends on
 * fonts that may not have loaded yet.
 */
export function wrapText(
  text: string, maxWidth: number, fontSize: number, maxLines = 3,
): string[] {
  const clean = String(text ?? "").trim();
  if (!clean) return [];
  const perChar = fontSize * 0.52;
  const fit = Math.max(4, Math.floor(maxWidth / perChar));
  if (clean.length <= fit) return [clean];

  const lines: string[] = [];
  let line = "";
  for (const word of clean.split(/\s+/)) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= fit) { line = candidate; continue; }
    if (line) lines.push(line);
    // A single word longer than the line is cut rather than allowed to run
    // out of the box; it is usually a path or a hyphenless compound.
    line = word.length > fit ? word.slice(0, fit - 1) + "…" : word;
    if (lines.length === maxLines) break;
  }
  if (line && lines.length < maxLines) lines.push(line);

  if (lines.length === maxLines) {
    // Whatever did not fit is shown as an ellipsis on the last line, so the
    // reader knows the value is longer than what they can see.
    const used = lines.join(" ").length;
    if (used < clean.length && !lines[maxLines - 1].endsWith("…")) {
      const last = lines[maxLines - 1];
      lines[maxLines - 1] = last.length >= fit
        ? last.slice(0, Math.max(1, fit - 1)) + "…"
        : last + "…";
    }
  }
  return lines;
}

/** A block's size, kept inside what is sensible however it was dragged. */
export function clampSize(w: number | undefined, min: number, max: number, fallback: number): number {
  const v = Number(w);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, v));
}
